import { action, computed, makeObservable, observable } from 'mobx';

import { ISettingsStore } from 'shared/entities/store/settingsStore';
import {
  CONVERSATION_AUTO_CLOSE_DEFAULT_MINUTES,
  CONVERSATION_AUTO_CLOSE_DEFAULT_UNIT,
  ConversationAutoCloseUnit,
  convertConversationAutoCloseToMinutes,
  validateConversationAutoCloseValue
} from 'shared/entities/projectSettings';
import { validatePositiveIntegerString } from 'shared/entities/validator';
import { FieldModel } from 'shared/models/form';

type PrivateFields =
  | '_unit'
  | '_lastSyncedUnit'
  | '_save'
  | 'completeSave'
  | 'resetToSavedValue';

export default class ConversationAutoCloseModel {
  private _unit: ConversationAutoCloseUnit;
  private _lastSyncedUnit: ConversationAutoCloseUnit;
  private pendingValue: number | null | undefined = undefined;
  private readonly settingsStore: ISettingsStore;
  readonly value: FieldModel<string>;

  constructor(settingsStore: ISettingsStore) {
    this.settingsStore = settingsStore;
    const initialMinutes =
      settingsStore.conversationAutoCloseDelayMinutes ??
      CONVERSATION_AUTO_CLOSE_DEFAULT_MINUTES;

    this._unit = CONVERSATION_AUTO_CLOSE_DEFAULT_UNIT;
    this._lastSyncedUnit = CONVERSATION_AUTO_CLOSE_DEFAULT_UNIT;
    this.value = new FieldModel(String(initialMinutes), [
      (value) => validateConversationAutoCloseValue(value, this.unit)
    ]);

    makeObservable<ConversationAutoCloseModel, PrivateFields>(this, {
      _unit: observable,
      _lastSyncedUnit: observable,

      unit: computed,
      lastSyncedUnit: computed,

      changeValue: action.bound,
      changeUnit: action.bound,
      commit: action.bound,
      enable: action.bound,
      disable: action.bound,
      _save: action.bound,
      completeSave: action.bound,
      resetToSavedValue: action.bound
    });
  }

  get unit(): ConversationAutoCloseUnit {
    return this._unit;
  }

  get lastSyncedUnit(): ConversationAutoCloseUnit {
    return this._lastSyncedUnit;
  }

  changeValue(nextValue: string): void {
    if (nextValue !== '' && validatePositiveIntegerString(nextValue)) {
      return;
    }

    this.value.changeValue(nextValue);
  }

  changeUnit(nextUnit: ConversationAutoCloseUnit): void {
    if (nextUnit === this.unit) {
      return;
    }

    this._unit = nextUnit;
    this.value.setTouched(true);
    this.value.validate();
    void this.commit();
  }

  commit = async (): Promise<void> => {
    if (this.value.validate()) {
      return;
    }

    const minutes = convertConversationAutoCloseToMinutes(
      Number(this.value.value),
      this.unit
    );

    if (
      !this.value.touched &&
      this.unit === this.lastSyncedUnit &&
      this.settingsStore.conversationAutoCloseDelayMinutes === minutes
    ) {
      return;
    }

    return this._save(minutes);
  };

  enable = async (): Promise<void> => {
    const defaultValue = String(CONVERSATION_AUTO_CLOSE_DEFAULT_MINUTES);

    this.value.changeValue(defaultValue);
    this._unit = CONVERSATION_AUTO_CLOSE_DEFAULT_UNIT;
    this.value.resetError();

    return this._save(CONVERSATION_AUTO_CLOSE_DEFAULT_MINUTES);
  };

  disable = (): Promise<void> => this._save(null);

  private async _save(value: number | null): Promise<void> {
    this.completeSave(await this.saveValue(value));
  }

  private async saveValue(value: number | null): Promise<BaseResponse | null> {
    if (this.settingsStore.conversationAutoCloseUpdating) {
      this.pendingValue = value;
      return null;
    }

    const response =
      await this.settingsStore.updateConversationAutoCloseDelayMinutes(value);
    const pendingValue = this.pendingValue;

    this.pendingValue = undefined;

    if (pendingValue !== undefined && pendingValue !== value) {
      return this.saveValue(pendingValue);
    }

    return response;
  }

  private completeSave(response: BaseResponse | null): void {
    if (!response) {
      return;
    }

    if (response.isError) {
      this.resetToSavedValue();
      return;
    }

    const savedValue = this.settingsStore.conversationAutoCloseDelayMinutes;

    if (savedValue === null) {
      this.value.resetError();
      this.value.setTouched(false);
      return;
    }

    if (
      convertConversationAutoCloseToMinutes(
        Number(this.value.value),
        this.unit
      ) === savedValue
    ) {
      this.markSynced();
    }
  }

  private resetToSavedValue(): void {
    const savedValue = this.settingsStore.conversationAutoCloseDelayMinutes;

    if (savedValue === null) {
      this.value.reset();
      this._unit = this.lastSyncedUnit;
      return;
    }

    this._unit = CONVERSATION_AUTO_CLOSE_DEFAULT_UNIT;
    this.value.changeValue(String(savedValue));
    this.value.resetTouched();
    this._lastSyncedUnit = CONVERSATION_AUTO_CLOSE_DEFAULT_UNIT;
  }

  private markSynced(): void {
    this.value.updateInitialValue();
    this.value.setTouched(false);
    this._lastSyncedUnit = this._unit;
  }
}
